import { useEffect } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';

export default function ExperiencePage() {
  useEffect(() => {
    document.title = 'Experience | Henry Adebisi';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Over a decade of leadership in finance and strategy across Africa\'s digital, investment, and trade ecosystems. Henry has worked with leading organizations including Jobberman, TATC, and Meristem, driving financial transformation and growth.');
    }
    
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', `${import.meta.env.VITE_SITE_URL || 'https://henryadebisi.com'}/experience`);
    }
    
    const keywords = document.querySelector('meta[name="keywords"]');
    if (keywords) {
      keywords.setAttribute('content', 'finance leadership, strategy, digital transformation, Jobberman, TATC, Meristem');
    }

    // Add Schema.org JSON-LD
    const existingSchema = document.querySelector('script[type="application/ld+json"]');
    if (existingSchema) {
      existingSchema.remove();
    }

    const schema = {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Experience | Henry Adebisi",
      "description": "Over a decade of leadership in finance and strategy across Africa's digital, investment, and trade ecosystems.",
      "url": `${import.meta.env.VITE_SITE_URL || 'https://henryadebisi.com'}/experience`,
      "mainEntity": {
        "@type": "Person",
        "name": "Henry Olamide Adebisi",
        "hasOccupation": [
          {
            "@type": "Occupation",
            "name": "Founder",
            "occupationLocation": {
              "@type": "Country",
              "name": "Nigeria"
            },
            "skills": ["Strategic Finance", "Fintech Innovation", "Investment Frameworks"]
          },
          {
            "@type": "Occupation",
            "name": "Group Head of Finance",
            "occupationLocation": {
              "@type": "Country",
              "name": "Nigeria"
            },
            "skills": ["Multi-country Operations", "Financial Planning", "Performance Management"]
          }
        ]
      }
    };

    const schemaScript = document.createElement('script');
    schemaScript.type = 'application/ld+json';
    schemaScript.textContent = JSON.stringify(schema);
    document.head.appendChild(schemaScript);

    return () => {
      const schemaToRemove = document.querySelector('script[type="application/ld+json"]');
      if (schemaToRemove) {
        schemaToRemove.remove();
      }
    };
  }, []);

  const experiences = [
    {
      title: 'Founder',
      company: 'AfriStakes',
      location: 'Nigeria',
      period: 'Mar 2020 - Present',
      description: 'Fintech initiative focused on bridging Africa\'s SME funding gap through digital frameworks connecting scalable businesses with structured capital.',
      achievements: [
        'Developing digital frameworks for SME funding solutions',
        'Creating equity, debt, and strategic advisory solutions',
        'Building connections between high-growth businesses and structured capital',
        'Championing funding for Africa\'s real economy growth'
      ]
    },
    {
      title: 'Co-Founder & Chief Financial Officer',
      company: 'Henatos Ventures',
      location: 'Tanzania & Nigeria',
      period: 'Mar 2020 - Present',
      description: 'Regional commodities-export and trading business connecting African producers with buyers across markets. Overseeing finance, strategy, and funding operations.',
      achievements: [
        'Built efficient value-chain systems across multiple African markets',
        'Established transparent financial reporting across multiple locations',
        'Developed strategic partnerships with producers and international buyers',
        'Led funding operations and financial strategy for regional expansion'
      ]
    },
    {
      title: 'Finance Manager',
      company: 'Visatier Limited',
      location: 'United Kingdom',
      period: 'Feb 2025 - Present',
      description: 'Overseeing financial planning, analysis, and investor reporting while supporting strategic growth initiatives and compliance with UK accounting standards.',
      achievements: [
        'Leading financial planning and analysis for strategic growth initiatives',
        'Managing investor reporting and stakeholder communications',
        'Ensuring compliance with UK accounting standards and regulations',
        'Supporting business development and expansion strategies'
      ]
    },
    {
      title: 'Group Head of Finance',
      company: 'The African Talent Company (TATC) - Jobberman\'s Parent Company',
      location: 'Nigeria, Ghana, Kenya, Uganda',
      period: '2024 - Jan 2025',
      description: 'Expanded oversight to four markets leading multi-country budgeting, reporting, and performance management for Africa\'s largest recruitment and HR-technology group.',
      achievements: [
        'Led multi-country financial operations across Nigeria, Ghana, Kenya, and Uganda',
        'Co-developed financial frameworks for youth-employment programs exceeding $50 million',
        'Strengthened group-wide governance structures and reporting systems',
        'Managed large-scale multi-partner investment initiatives'
      ]
    },
    {
      title: 'Head of Finance',
      company: 'Jobberman Nigeria',
      location: 'Nigeria',
      period: '2020 - 2023',
      description: 'Led the company\'s financial turnaround, implementing new business models, data-driven cost controls, and strategic forecasting that improved profitability and sustainability.',
      achievements: [
        'Led financial turnaround and profitability improvements',
        'Implemented new business models and data-driven cost controls',
        'Managed donor-funded initiatives with Mastercard Foundation, UNICEF, USAID, and UNDP',
        'Ensured fiscal discipline and program compliance across all initiatives'
      ]
    },
    {
      title: 'Other Finance Leadership Roles',
      company: 'Meristem Securities, RenMoney & Opay',
      location: 'Nigeria',
      period: '2015 - 2020',
      description: 'Gained institutional finance experience developing expertise in financial reporting, treasury management, risk control, and audit readiness.',
      achievements: [
        'Developed deep expertise in financial reporting and treasury management',
        'Built strong foundation in risk control and audit readiness',
        'Gained experience across diverse financial services sectors',
        'Shaped leadership approach to finance strategy and transformation'
      ]
    }
  ];

  const highlights = [
    {
      icon: 'ri-global-line',
      title: '11+ Years Experience',
      description: 'Leading financial transformation across Africa and the UK'
    },
    {
      icon: 'ri-team-line',
      title: 'Multi-Market Leadership',
      description: 'Operations across Nigeria, Ghana, Kenya, Uganda, Tanzania, and UK'
    },
    {
      icon: 'ri-line-chart-line',
      title: '$50M+ Programs',
      description: 'Co-developed frameworks for large-scale investment initiatives'
    },
    {
      icon: 'ri-building-line',
      title: 'Venture Builder',
      description: 'Co-founded Henatos Ventures and founded AfriStakes'
    }
  ];

  const qualifications = [
    {
      title: 'Chartered Accountant (ACA)',
      institution: 'Institute of Chartered Accountants of Nigeria (ICAN)',
      achievement: 'ICAN Silver Scholarship recipient'
    },
    {
      title: 'Associate Accounting Technician (AAT)',
      institution: 'Institute of Chartered Accountants of Nigeria (ICAN)',
      achievement: 'Best Qualifying Male Student (AATS 3) award'
    },
    {
      title: 'BSc in Accounting',
      institution: 'Lagos State University',
      achievement: 'Strong academic foundation in financial principles'
    }
  ];

  return (
    <div className="min-h-screen bg-off-white">
      <Header />
      
      <main>
        {/* Hero Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="font-sora font-bold text-4xl lg:text-5xl text-navy mb-6">
              Professional Experience
            </h1>
            <p className="text-gray-600 text-lg max-w-3xl mx-auto leading-relaxed">
              Over eleven years of experience leading financial transformation, cross-border operations, 
              and impact partnerships across Africa and the United Kingdom.
            </p>
          </div>
        </section>

        {/* Highlights Section */}
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-20">
              {highlights.map((highlight, index) => (
                <div key={index} className="bg-white rounded-lg p-6 shadow-lg text-center">
                  <div className="w-12 h-12 bg-gold/10 rounded-lg flex items-center justify-center mx-auto mb-4">
                    <i className={`${highlight.icon} text-2xl text-gold`}></i>
                  </div>
                  <h3 className="font-sora font-semibold text-lg text-navy mb-2">
                    {highlight.title}
                  </h3>
                  <p className="text-gray-600 text-sm">
                    {highlight.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Timeline Section */}
        <section className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="font-sora font-bold text-3xl text-navy text-center mb-16">
              Career Timeline
            </h2>
            
            <div className="relative">
              {/* Timeline line */}
              <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-gold"></div>
              
              <div className="space-y-12">
                {experiences.map((exp, index) => (
                  <div key={index} className="relative flex items-start">
                    {/* Timeline dot */}
                    <div className="absolute left-6 w-4 h-4 bg-gold rounded-full border-4 border-white shadow-lg"></div>
                    
                    {/* Content */}
                    <div className="ml-20 bg-off-white rounded-lg p-8 shadow-lg">
                      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-4">
                        <div>
                          <h3 className="font-sora font-bold text-xl text-navy mb-1">
                            {exp.title}
                          </h3>
                          <p className="font-semibold text-gold mb-1">
                            {exp.company}
                          </p>
                          <p className="text-gray-500 text-sm mb-2">
                            {exp.location}
                          </p>
                        </div>
                        <span className="text-gray-500 font-medium">
                          {exp.period}
                        </span>
                      </div>
                      
                      <p className="text-gray-700 mb-6 leading-relaxed">
                        {exp.description}
                      </p>
                      
                      <div>
                        <h4 className="font-sora font-semibold text-navy mb-3">Key Achievements:</h4>
                        <ul className="space-y-2">
                          {exp.achievements.map((achievement, achievementIndex) => (
                            <li key={achievementIndex} className="flex items-start text-gray-600">
                              <i className="ri-check-line text-gold mr-3 mt-0.5 flex-shrink-0"></i>
                              <span>{achievement}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Qualifications Section */}
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="font-sora font-bold text-3xl text-navy text-center mb-16">
              Professional Qualifications
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {qualifications.map((qual, index) => (
                <div key={index} className="bg-white rounded-lg p-8 shadow-lg text-center">
                  <div className="w-16 h-16 bg-gold/10 rounded-full flex items-center justify-center mx-auto mb-6">
                    <i className="ri-award-line text-3xl text-gold"></i>
                  </div>
                  <h3 className="font-sora font-bold text-lg text-navy mb-2">
                    {qual.title}
                  </h3>
                  <p className="text-gray-600 mb-3">
                    {qual.institution}
                  </p>
                  <p className="text-gold text-sm font-medium">
                    {qual.achievement}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-navy">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="font-sora font-bold text-3xl lg:text-4xl text-white mb-6">
              Ready to Leverage This Experience?
            </h2>
            <p className="text-gray-300 text-lg mb-8 leading-relaxed">
              Bring Henry's proven expertise in financial transformation and strategic leadership to your organization.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button variant="secondary" size="lg" href="/henry-adebisi-cv.pdf" className="inline-flex items-center">
                <i className="ri-download-line mr-2"></i>
                Download Full CV
              </Button>
              <Button variant="outline" size="lg" href="/contact" className="border-white text-white hover:bg-white hover:text-navy bg-white/20 backdrop-blur-sm font-semibold">
                Discuss Opportunities
              </Button>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
